import React from 'react';
import PropTypes from 'prop-types';

import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import SectionTitle from '~/components/Typography/SectionTitle';

import { mqTablet } from '~/variables/breakpoints';
import { Box } from '@mui/material';

const styles = {
  panelWrapper: {
    boxShadow: 'none',
    marginBottom: '10px',
    flex: 1,
    width: '100%',
    border: '2px solid #efefef',
    borderRadius: '5px',
    maxWidth: '95%',
    [mqTablet]: {
      width: '100%',
    },
    '&:before': {
      display: 'none',
    },
  },
  panelSummary: {
    alignItems: 'center',
    cursor: 'auto',
    margin: '0  !important',
    minHeight: 48,
    width: '100%',
    '&:hover:not(.Mui-disabled)': {
      cursor: 'auto',
    },
  },
  panelDetails: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    margin: '0 !important',
    width: '100%',
    gap: 1,
    backgroundColor: (theme) => theme.palette.grey[50],
  },
  fullWidthPanel: {
    padding: '10px 20px !important',
  },
  icon: {
    padding: '0 !important',
    cursor: 'pointer',
    marginLeft: 'auto',
  },
  expanded: {
    margin: '0 !important',
    transform: 'rotate(180deg)',
  },
};

function StyledAccordion({
  children,
  title,
  initialState = true,
  fullWidth = false,
  deleteButton,
  summaryAction,
  alignBody = 'center',
  expanded: expandedProp,
  onChange,
  slotProps = {},
}) {
  const [internalExpanded, setInternalExpanded] = React.useState(initialState);
  const isControlled = expandedProp !== undefined;
  const expanded = isControlled ? expandedProp : internalExpanded;

  const handleToggle = () => {
    const next = !expanded;
    if (isControlled && onChange) {
      onChange(next);
    } else {
      setInternalExpanded(next);
    }
  };

  return (
    <Accordion sx={styles.panelWrapper} expanded={expanded}>
      <AccordionSummary sx={styles.panelSummary}>
        {title && (
          <SectionTitle
            style={{ cursor: 'pointer', whiteSpace: 'nowrap', margin: 0 }}
            onClick={handleToggle}
          >
            {title}
          </SectionTitle>
        )}
        <Box sx={{ flexGrow: 1 }} />
        {summaryAction ? (
          <Box sx={{ display: 'flex', alignItems: 'center', mr: 0.5 }}>{summaryAction}</Box>
        ) : null}
        <ExpandMoreIcon
          onClick={handleToggle}
          sx={{ ...styles.icon, ...(expanded ? styles.expanded : {}) }}
        />
        {deleteButton}
      </AccordionSummary>
      <AccordionDetails
        sx={{
          ...styles.panelDetails,
          ...(fullWidth ? styles.fullWidthPanel : {}),
          ...(alignBody === 'left' ? { alignItems: 'stretch' } : {}),
          ...slotProps?.details?.sx,
        }}
      >
        {children}
      </AccordionDetails>
    </Accordion>
  );
}

StyledAccordion.propTypes = {
  children: PropTypes.node,
  title: PropTypes.string,
  initialState: PropTypes.bool,
  fullWidth: PropTypes.bool,
  deleteButton: PropTypes.node,
  summaryAction: PropTypes.node,
  alignBody: PropTypes.oneOf(['center', 'left']),
  expanded: PropTypes.bool,
  onChange: PropTypes.func,
  // eslint-disable-next-line react/forbid-prop-types
  slotProps: PropTypes.object,
};
StyledAccordion.defaultProps = {
  children: null,
  title: '',
  initialState: true,
  fullWidth: false,
  deleteButton: null,
  summaryAction: null,
  alignBody: 'center',
  expanded: undefined,
  onChange: null,
  slotProps: {},
};
export default StyledAccordion;
